import React, { useMemo } from "react";
import { graphql, useFragment } from "react-relay";
import TextArea from "react-textarea-autosize";

import {
	faCheck,
	faClone,
	faInfoCircle,
	faPen,
	faSpinnerThird,
	faThumbsDown,
	faThumbsUp,
	faTimes,
} from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import {
	faEllipsisV,
	faLocation,
	faMagnifyingGlassPlus,
	faPlus,
	faTrashCan,
} from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { faChevronDown, faThumbsUp as faThumbsUpSolid } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import * as Sentry from "@sentry/browser";
import cx from "classnames";
import { isEmpty, isNil, noop } from "lodash";
// Add Markdown import here:
import Markdown from "markdown-to-jsx";
import moment from "moment";
import invariant from "tiny-invariant";

import { AppContext } from "~/common";
import { commitMutationPromise } from "~/lib/relayUtil";
import { dispatchAnalyticsTrack } from "~/lib/segment";
import { decapitalizeFirstLetter } from "~/lib/utils";
import { ChaptersPanel, type ChaptersPanelRef } from "~/meetingRecorder/ChaptersPanel";
import { ContextPopover } from "~/meetingRecorder/ContextPopover";
import { environment } from "~/models";
import { Button, Tooltip } from "~/ui";
import { IconButton } from "~/ui/button/IconButton";
import BetaLabel from "~/ui/labels/BetaLabel";
import { ButtonLink } from "~/ui/legacyFellowIndex";
import LoadingSpinner from "~/ui/loading/LoadingSpinner";
import Menu from "~/ui/menu";
import Selector, { type SingleValue } from "~/ui/selectors/Selector";
import toasty from "~/ui/toast";

import { BillingContext } from "../account/Billing/BillingContext";
import { showFeatureModal } from "../billing";
import { streamStore } from "../stream/utils";
import { useTrackersFeatureFlagOn } from "../trackers/featureFlag";
import AIActionItemsList from "./AIActionItems";
import { SummaryText } from "./SummaryText";
import { RecordingContext, TranscriptModalContext } from "./TranscriptModalContext";
import {
	LowLanguageConfidenceRegenerationBanner,
	type LowLanguageConfidenceScoreAnalyticsDataType,
	openRelabelMultipleSpeakersModal,
	UnlabeledSpeakersWarning,
	VideoTimestamp,
} from "./TranscriptPanel";
import {
	actionItemToText,
	emptyHighlightSectionText,
	formatTimestamp,
	HighlightFeedbackSource,
	type KeyMoment,
	keyMomentsToText,
	type RecapTemplate,
	TEMPLATE_OPTIONS,
	type TemplateOption,
	useIsNarrowViewport,
	useIsPublic,
	useRealAiActionItemsIsOn,
} from "./utils";

import type { HighlightsPanel_DecisionsHighlight_decisions$key } from "~/__generated__/HighlightsPanel_DecisionsHighlight_decisions.graphql";
import type {
	HighlightsPanel_meetingRecording$key,
	HighlightsPanel_meetingRecording$data as MeetingRecording,
} from "~/__generated__/HighlightsPanel_meetingRecording.graphql";
import type { HighlightsPanel_note$key } from "~/__generated__/HighlightsPanel_note.graphql";
import type { HighlightsPanel_viewer$key } from "~/__generated__/HighlightsPanel_viewer.graphql";
import type { HighlightsPanelCreateActionItemMutation } from "~/__generated__/HighlightsPanelCreateActionItemMutation.graphql";
import type { HighlightsPanelDeleteActionItemsMutation } from "~/__generated__/HighlightsPanelDeleteActionItemsMutation.graphql";
import type { HighlightsPanelSaveActionItemsMutation } from "~/__generated__/HighlightsPanelSaveActionItemsMutation.graphql";
import type {
	HighlightsPanelSaveDecisionsMutation,
	HighlightsPanelSaveDecisionsMutation$data,
} from "~/__generated__/HighlightsPanelSaveDecisionsMutation.graphql";
import type {
	HighlightsPanelSaveKeyMomentsMutation,
	HighlightsPanelSaveKeyMomentsMutation$data,
} from "~/__generated__/HighlightsPanelSaveKeyMomentsMutation.graphql";
import type {
	HighlightsPanelSaveSummaryMutation,
	HighlightsPanelSaveSummaryMutation$data,
} from "~/__generated__/HighlightsPanelSaveSummaryMutation.graphql";
import type { RecapTemplateType } from "~/__generated__/TranscriptModalGenerateRecapTemplateMutation.graphql";

import chapterStyles from "./css/ChaptersPanel.module.css";
import styles from "./css/HighlightsPanel.module.css";
import transcriptStyles from "./css/TranscriptPanel.module.css";

export type TranscriptActionItems = NonNullable<MeetingRecording["activeSummary"]>["actionItems"];
export type TranscriptActionItem = NonNullable<TranscriptActionItems>[number];
type MeetingRecordingSmartTrackers = NonNullable<MeetingRecording["smartTrackers"]>;

/**
 * @param {string} title The title of the section
 * @param  secondaryActions - A React element to be rendered on the right side of the section header
 */
export const HighlightSection = ({
	title,
	isCollapsed,
	toggleCollapsed,
	counter,
	tooltipContent,
	children,
	secondaryActions,
	isBeta,
	dataTourTooltipTarget,
}: React.PropsWithChildren<{
	title: string | React.ReactNode;
	isCollapsed: boolean;
	toggleCollapsed: () => void;
	counter?: number;
	tooltipContent?: string;
	secondaryActions?: React.ReactElement[];
	isBeta?: boolean;
	dataTourTooltipTarget?: string;
}>) => {
	// UX Simplification
	const { user } = React.useContext(AppContext);
	const uxSimplificationEnabled = user?.uxSimplificationEnabled;

	if (uxSimplificationEnabled) {
		return (
			<section
				className={cx(styles.highlightsContainer, { [styles.simplifiedUxContainer]: uxSimplificationEnabled })}
				data-tour-tooltip-target={dataTourTooltipTarget}
			>
				<div className={styles.sectionHeader}>
					<div className={styles.sectionTitle}>{typeof title === "string" ? <p>{title}</p> : title}</div>
					{tooltipContent && (
						<Tooltip maxWidth={270} content={tooltipContent}>
							<FontAwesomeIcon icon={faInfoCircle} className={styles.infoIcon} />
						</Tooltip>
					)}
					<div className={styles.secondaryActions}>
						{secondaryActions && secondaryActions.map(secondaryAction => secondaryAction)}
					</div>
				</div>

				{children}
			</section>
		);
	}

	return (
		<section className={styles.highlightsContainer}>
			<div className={styles.sectionHeader}>
				<button
					className={styles.collapseButton}
					onClick={toggleCollapsed}
					data-tour-tooltip-target={dataTourTooltipTarget}
				>
					<FontAwesomeIcon
						fixedWidth
						icon={faChevronDown}
						className={cx(styles.chevron, { [styles.collapsed]: isCollapsed })}
					/>
					{typeof title === "string" ? <p>{title}</p> : title}
					{isBeta && <BetaLabel />}
					{tooltipContent && (
						<Tooltip maxWidth={270} content={tooltipContent}>
							<FontAwesomeIcon icon={faInfoCircle} className={styles.infoIcon} />
						</Tooltip>
					)}
					{counter && counter > 0 && <span className={styles.counter}>{counter}</span>}
				</button>
				<div className={styles.secondaryActions}>
					{secondaryActions && secondaryActions.map(secondaryAction => secondaryAction)}
				</div>
			</div>
			{!isCollapsed ? children : null}
		</section>
	);
};

const AddButton = ({ onClick }: { onClick: () => void }) => {
	return (
		<Tooltip content="Add">
			<IconButton size="small" variant="tertiary" title="Add" icon={faPlus} onClick={onClick} />
		</Tooltip>
	);
};

const EditButton = ({ onClick }: { onClick: () => void }) => {
	return (
		<Tooltip content="Edit">
			<IconButton size="small" variant="tertiary" title="Edit" icon={faPen} onClick={onClick} />
		</Tooltip>
	);
};

const CopyButton = ({ onClick }: { onClick: () => void }) => {
	return (
		<Tooltip content="Copy">
			<IconButton size="small" variant="tertiary" title="Copy" icon={faClone} onClick={onClick} />
		</Tooltip>
	);
};

export function clearLikedSectionCacheForRecording(recordingId: string) {
	for (const section of Object.values(HighlightFeedbackSource)) {
		streamStore.remove(getLikedCacheKeyForSection(recordingId, section));
	}
}

function getLikedCacheKeyForSection(recordingId: string, sectionName: HighlightFeedbackSource) {
	return `liked-${recordingId}-${sectionName}`;
}

export const RegenerateButtons = ({
	clickDislike,
	sectionName,
	recordingId,
	recordingTitle,
}: {
	clickDislike: (source: HighlightFeedbackSource) => void;
	sectionName: HighlightFeedbackSource;
	recordingId: string;
	recordingTitle: string;
}) => {
	const { analyticsFingerprint } = React.useContext(BillingContext);

	const thumbsUpCacheKey = React.useMemo(
		() => getLikedCacheKeyForSection(recordingId, sectionName),
		[recordingId, sectionName],
	);
	const [thumbsUpClicked, setThumbsUpClicked] = React.useState<boolean>(streamStore.get(thumbsUpCacheKey) !== null);
	const [justClicked, setJustClicked] = React.useState<boolean>(false);

	const onClickLike = React.useCallback(() => {
		if (!thumbsUpClicked) {
			dispatchAnalyticsTrack("[AI-Copilot] Like Clicked", {
				fingerprint: analyticsFingerprint,
				section: sectionName,
				recordingId: recordingId,
				recordingTitle: recordingTitle,
			});
			streamStore.setExpire(thumbsUpCacheKey, moment(), moment().add(6, "months"));
			setJustClicked(true);
		} else {
			streamStore.remove(thumbsUpCacheKey);
		}
		setThumbsUpClicked(prev => !prev);
	}, [analyticsFingerprint, recordingId, recordingTitle, sectionName, thumbsUpCacheKey, thumbsUpClicked]);

	const onClickDislike = React.useCallback(() => {
		streamStore.remove(thumbsUpCacheKey);
		clickDislike(sectionName);
	}, [clickDislike, sectionName, thumbsUpCacheKey]);

	return (
		<>
			<Tooltip content="Like">
				<IconButton
					title="Like"
					variant="tertiary"
					size="small"
					icon={thumbsUpClicked ? faThumbsUpSolid : faThumbsUp}
					className={cx(styles.likeButton, { [styles.liked]: justClicked })}
					onClick={onClickLike}
					key={`like-${sectionName}`}
				/>
			</Tooltip>
			{!thumbsUpClicked && (
				<Tooltip content="Regenerate">
					<IconButton
						title="Dislike"
						variant="tertiary"
						size="small"
						icon={faThumbsDown}
						className={styles.thumbsDown}
						onClick={onClickDislike}
						key={`dislike-${sectionName}`}
					/>
				</Tooltip>
			)}
		</>
	);
};

const saveSummaryMutation = graphql`
	mutation HighlightsPanelSaveSummaryMutation($recordingId: ID!, $summary: String!) {
		saveTranscriptSummary(recordingId: $recordingId, summary: $summary) {
			meetingRecording {
				...HighlightsPanel_meetingRecording @arguments(public: false)
			}
		}
	}
`;

const saveDecisionsMutation = graphql`
	mutation HighlightsPanelSaveDecisionsMutation($recordingId: ID!, $decisionsText: String!) {
		saveDecisions(recordingId: $recordingId, decisionsText: $decisionsText) {
			validationError
			meetingRecording {
				id
				activeSummary {
					decisions {
						timestamp
						text
					}
				}
				...HighlightsPanel_meetingRecording @arguments(public: false)
			}
		}
	}
`;

const saveActionItemsMutation = graphql`
	mutation HighlightsPanelSaveActionItemsMutation(
		$recordingId: ID!
		$prevActionItemTimestamp: Float!
		$actionItemText: String!
		$isNew: Boolean!
	) {
		saveTranscriptActionItem(
			recordingId: $recordingId
			prevActionItemTimestamp: $prevActionItemTimestamp
			actionItemText: $actionItemText
			isNew: $isNew
		) {
			validationError
			meetingRecording {
				activeSummary {
					actionItems {
						timestamp
						text
						assignees {
							userId
							fullName
						}
						insertedBlockKey
						edited
					}
				}
				...HighlightsPanel_meetingRecording @arguments(public: false)
			}
		}
	}
`;

const deleteActionItemsMutation = graphql`
	mutation HighlightsPanelDeleteActionItemsMutation($recordingId: ID!, $actionItemTimestamp: Float!) {
		deleteTranscriptActionItem(recordingId: $recordingId, actionItemTimestamp: $actionItemTimestamp) {
			meetingRecording {
				...HighlightsPanel_meetingRecording @arguments(public: false)
			}
		}
	}
`;

const insertActionItemMutation = graphql`
	mutation HighlightsPanelCreateActionItemMutation($input: CreateTodoFromTranscriptInput!) {
		createTodoFromTranscript(input: $input) {
			meetingRecording {
				...HighlightsPanel_meetingRecording @arguments(public: false)
			}
		}
	}
`;

const saveKeyMomentsMutation = graphql`
	mutation HighlightsPanelSaveKeyMomentsMutation(
		$recordingId: ID!
		$templateType: RecapTemplateType!
		$templateSection: String!
		$keyMomentsText: String!
	) {
		saveKeyMoments(
			recordingId: $recordingId
			templateType: $templateType
			templateSection: $templateSection
			keyMomentsText: $keyMomentsText
		) {
			validationError
			meetingRecording {
				id
				activeSummary {
					activeRecapTemplate
					templatedRecaps
				}
				...HighlightsPanel_meetingRecording @arguments(public: false)
			}
		}
	}
`;

function saveActiveTemplate(meetingRecordingId: string, templateType: RecapTemplateType | null = null) {
	commitMutationPromise(environment, {
		mutation: graphql`
			mutation HighlightsPanelSaveActiveTemplateMutation($meetingRecordingId: ID!, $templateType: RecapTemplateType) {
				saveActiveTemplate(meetingRecordingId: $meetingRecordingId, templateType: $templateType) {
					meetingRecording {
						activeSummary {
							activeRecapTemplate
						}
					}
				}
			}
		`,
		variables: {
			meetingRecordingId,
			templateType,
		},
	});
}

const GenericHighlight = ({
	sectionName,
	keyMoments,
	videoDuration,
	onTimestampClick,
	onUpdateKeyMoments,
	templateType,
	readOnly,
}: {
	sectionName: string;
	templateType: RecapTemplateType;
	keyMoments: KeyMoment[];
	videoDuration: number | null;
	onTimestampClick?: (timestamp: number) => void;
	onUpdateKeyMoments?: (keyMomentsText: string) => Promise<HighlightsPanelSaveKeyMomentsMutation$data>;
	readOnly: boolean;
}) => {
	const keyMomentsIsEmpty = keyMoments.length === 0;
	const [editMode, setEditMode] = React.useState<boolean>(false);
	const [keyMomentsText, setKeyMomentsText] = React.useState<string>(keyMomentsToText(keyMoments ?? [], videoDuration));
	const [loading, setLoading] = React.useState<boolean>(false);
	const [error, setError] = React.useState<string | null>(null);
	const prevKeyMomentsText = React.useRef<string>(keyMomentsText);
	const textareaRef = React.useRef<HTMLTextAreaElement>(null);

	const { analyticsFingerprint, noteId, meetingRecordingSummaryId } = React.useContext(TranscriptModalContext);

	const [isCollapsed, setIsCollapsed] = React.useState<boolean>(false);
	const toggleCollapsed = React.useCallback(() => {
		if (editMode) return;
		setIsCollapsed(prevIsCollapsed => !prevIsCollapsed);
	}, [editMode]);

	React.useEffect(() => {
		// Updates received from other users' edits
		if (!editMode) {
			const newKeyMoments = keyMomentsToText(keyMoments ?? [], videoDuration);
			setKeyMomentsText(newKeyMoments);
			prevKeyMomentsText.current = newKeyMoments;
		}
	}, [editMode, keyMoments, videoDuration]);

	const onEditKeyMoments = React.useCallback(() => {
		if (readOnly) return;
		setEditMode(true);
		setIsCollapsed(false);

		// Hacky way to set the cursor to the end of the textarea when editing
		// The textarea is not rendered until editMode is true, so we need to wait
		// for the element to render to have access to the ref
		setTimeout(() => {
			textareaRef.current?.setSelectionRange(keyMomentsText.length, keyMomentsText.length);
			textareaRef.current?.scrollIntoView({ block: "center" });
		}, 0);
		dispatchAnalyticsTrack("[AI Copilot] Recap Edited", {
			fingerprint: analyticsFingerprint,
			action: "Started",
			/*  eslint-disable camelcase */
			recap_component: "Custom Section",
			note_id: noteId,
			meeting_recording_summary_id: meetingRecordingSummaryId,
			/*  eslint-enable camelcase */
		});
	}, [analyticsFingerprint, keyMomentsText.length, meetingRecordingSummaryId, noteId, readOnly]);

	const onChangeKeyMomentsText = React.useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
		setKeyMomentsText(event.target.value);
	}, []);

	const onInputKeyMomentsText = React.useCallback((): void => {
		setError(null);
	}, []);

	const onSaveEdit = React.useCallback(() => {
		if (readOnly) return;
		setLoading(true);
		onUpdateKeyMoments?.(keyMomentsText)
			.then(res => {
				setLoading(false);

				if (!res.saveKeyMoments) {
					toasty.error("Something went wrong, failed to update chapters");
					return;
				}

				const { validationError, meetingRecording } = res.saveKeyMoments;

				if (validationError) {
					setError(validationError);
				} else if (meetingRecording?.activeSummary?.templatedRecaps) {
					setEditMode(false);
					prevKeyMomentsText.current = keyMomentsToText(
						meetingRecording.activeSummary.templatedRecaps[templateType][sectionName],
						videoDuration,
					);
					toasty.success("Key moments updated successfully");
					dispatchAnalyticsTrack("[AI Copilot] Recap Edited", {
						fingerprint: analyticsFingerprint,
						action: "Saved",
						/*  eslint-disable camelcase */
						recap_component: "Custom Section",
						note_id: noteId,
						meeting_recording_summary_id: meetingRecordingSummaryId,
						/*  eslint-enable camelcase */
					});
				}
			})
			.catch(errors => {
				setLoading(false);
				Sentry.captureException(errors[0]);
				toasty.error("Something went wrong, failed to update decisions");
			});
	}, [
		analyticsFingerprint,
		keyMomentsText,
		meetingRecordingSummaryId,
		noteId,
		onUpdateKeyMoments,
		readOnly,
		sectionName,
		templateType,
		videoDuration,
	]);

	const onCancelEdit = React.useCallback(() => {
		setEditMode(false);
		setError(null);
		setKeyMomentsText(prevKeyMomentsText.current);
		dispatchAnalyticsTrack("[AI Copilot] Recap Edited", {
			fingerprint: analyticsFingerprint,
			action: "Cancelled",
			/*  eslint-disable camelcase */
			recap_component: "Custom Section",
			note_id: noteId,
			meeting_recording_summary_id: meetingRecordingSummaryId,
			/*  eslint-enable camelcase */
		});
	}, [analyticsFingerprint, meetingRecordingSummaryId, noteId]);

	const { copySection } = React.useContext(RecordingContext);
	const onCopyContent = React.useCallback(async () => {
		copySection("Custom", { customSectionName: sectionName });
	}, [copySection, sectionName]);

	const secondaryActions = React.useMemo(() => {
		if (editMode || (readOnly && keyMomentsIsEmpty)) {
			return undefined;
		}

		if (keyMomentsIsEmpty) {
			return [<AddButton key="add-button" onClick={onEditKeyMoments} />];
		} else if (readOnly) {
			return [<CopyButton key="copy-button" onClick={onCopyContent} />];
		} else {
			return [
				<CopyButton key="copy-button" onClick={onCopyContent} />,
				<EditButton key="edit-button" onClick={onEditKeyMoments} />,
			];
		}
	}, [readOnly, editMode, keyMomentsIsEmpty, onEditKeyMoments, onCopyContent]);

	return (
		<HighlightSection
			title={sectionName}
			isCollapsed={isCollapsed}
			toggleCollapsed={toggleCollapsed}
			counter={keyMoments.length > 0 ? keyMoments.length : undefined}
			secondaryActions={secondaryActions}
		>
			{keyMomentsIsEmpty && !editMode ? (
				<div className={styles.highlightEmptyState}>
					{emptyHighlightSectionText(videoDuration, "key moments")}
					{!readOnly && (
						<>
							To <ButtonLink onClick={onEditKeyMoments}>add a key moment,</ButtonLink> add a timestamp in brackets
							followed by a title, e.g. {`[${(videoDuration ?? 0) >= 3600 ? "00:" : ""}01:23] Key Moment One`}
						</>
					)}
				</div>
			) : (
				<>
					{editMode ? (
						<>
							<main className={cx(styles.textAreaWrapper, { [styles.error]: error })}>
								<TextArea
									autoFocus
									ref={textareaRef}
									value={keyMomentsText}
									onChange={onChangeKeyMomentsText}
									onInput={onInputKeyMomentsText}
									className={styles.textArea}
									minRows={4}
									placeholder={`[${(videoDuration ?? 0) >= 3600 ? "00:" : ""}00:00] Add a key moment`}
								/>
								<div className={styles.btnWrapper}>
									<Button variant="tertiary" className={styles.actionBtn} disabled={loading} onClick={onCancelEdit}>
										<FontAwesomeIcon icon={faTimes} />
									</Button>
									<Button
										variant="primary"
										className={styles.actionBtn}
										disabled={!!error || loading || keyMomentsText === prevKeyMomentsText.current}
										onClick={onSaveEdit}
									>
										{loading ? <FontAwesomeIcon icon={faSpinnerThird} spin /> : <FontAwesomeIcon icon={faCheck} />}
									</Button>
								</div>
							</main>
							{error ? <p className={styles.errorText}>{error}</p> : null}
						</>
					) : (
						<main className={styles.keyPointsWrapper}>
							<ul className={styles.keyPointsBullets}>
								{keyMoments.length > 0 &&
									keyMoments.map((keyMoment, index) => (
										// eslint-disable-next-line react/no-array-index-key
										<li key={`keyMoment-item-${index}`}>
											<section key={`${keyMoment.text}-${keyMoment.timestamp}`} className={styles.keyPointsSection}>
												<span className={styles.chapterTitle}>{keyMoment.text}</span>
												<VideoTimestamp
													className={chapterStyles.chapterTimestamp}
													timestamp={keyMoment.timestamp}
													videoDuration={videoDuration}
													onClick={onTimestampClick}
													isInlineTimestamp={true}
												/>
											</section>
										</li>
									))}
							</ul>
						</main>
					)}
				</>
			)}
		</HighlightSection>
	);
};

export const SummaryHighlight = ({
	summary: propSummary,
	recordingDuration,
	onUpdateSummary,
	mode,
	feedbackButtons,
}: {
	summary: string;
	recordingDuration: number | null;
	onUpdateSummary?: (summary: string) => Promise<HighlightsPanelSaveSummaryMutation$data>;
	mode: "edit" | "readonly";
	feedbackButtons?: (sectionName: HighlightFeedbackSource) => JSX.Element;
}) => {
	// UX Simplification
	const { user } = React.useContext(AppContext);
	const uxSimplificationEnabled = user?.uxSimplificationEnabled;

	const [summary, setSummary] = React.useState<string>(propSummary);
	const [editMode, setEditMode] = React.useState<boolean>(false);
	const prevSummary = React.useRef<string>(propSummary);
	const textareaRef = React.useRef<HTMLTextAreaElement>(null);
	const { analyticsFingerprint, noteId, meetingRecordingSummaryId } = React.useContext(TranscriptModalContext);

	const summaryIsEmpty = summary === "";

	const summarySectionRef = React.useRef<HTMLDivElement>(null);

	const [isCollapsed, setIsCollapsed] = React.useState<boolean>(false);
	const toggleCollapsed = React.useCallback(() => {
		if (editMode) return;
		setIsCollapsed(!isCollapsed);
	}, [editMode, isCollapsed]);

	React.useEffect(() => {
		// Updates received from other users' edits
		if (!editMode) {
			setSummary(propSummary);
			prevSummary.current = propSummary;
		}
	}, [editMode, propSummary, summary]);

	const onEditSummary = React.useCallback(() => {
		if (mode === "readonly") return;
		setEditMode(true);
		setIsCollapsed(false);

		// Hacky way to set the cursor to the end of the textarea when editing
		// The textarea is not rendered until editMode is true, so we need to wait
		// for for the element to render to have access to the ref
		setTimeout(() => {
			textareaRef.current?.setSelectionRange(summary.length, summary.length);
		}, 0);
		dispatchAnalyticsTrack("[AI Copilot] Recap Edited", {
			fingerprint: analyticsFingerprint,
			action: "Started",
			/*  eslint-disable camelcase */
			recap_component: "Summary",
			note_id: noteId,
			meeting_recording_summary_id: meetingRecordingSummaryId,
			/*  eslint-enable camelcase */
		});
	}, [analyticsFingerprint, meetingRecordingSummaryId, noteId, mode, summary.length]);

	const { copySection } = React.useContext(RecordingContext);
	const onCopySummary = React.useCallback(() => {
		copySection("Summary");
	}, [copySection]);
	const onCopyAll = React.useCallback(() => {
		copySection("All");
	}, [copySection]);

	const onChangeSummary = React.useCallback((event: React.ChangeEvent<HTMLTextAreaElement>): void => {
		setSummary(event.target.value);
	}, []);

	const onSaveEdit = React.useCallback(() => {
		if (mode !== "edit") return;
		onUpdateSummary?.(summary)
			.then(() => {
				setEditMode(false);
				prevSummary.current = summary;
				toasty.success("Summary updated successfully");
				summarySectionRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
				dispatchAnalyticsTrack("[AI Copilot] Recap Edited", {
					fingerprint: analyticsFingerprint,
					action: "Saved",
					/*  eslint-disable camelcase */
					recap_component: "Summary",
					note_id: noteId,
					meeting_recording_summary_id: meetingRecordingSummaryId,
					/*  eslint-enable camelcase */
				});
			})
			.catch(e => {
				Sentry.captureException(e);
				toasty.error("Failed to update summary");
			});
	}, [analyticsFingerprint, meetingRecordingSummaryId, noteId, onUpdateSummary, mode, summary]);

	const onCancelEdit = React.useCallback(() => {
		setEditMode(false);
		setSummary(prevSummary.current);
		dispatchAnalyticsTrack("[AI Copilot] Recap Edited", {
			fingerprint: analyticsFingerprint,
			action: "Cancelled",
			/*  eslint-disable camelcase */
			recap_component: "Summary",
			note_id: noteId,
			meeting_recording_summary_id: meetingRecordingSummaryId,
			/*  eslint-enable camelcase */
		});
	}, [analyticsFingerprint, meetingRecordingSummaryId, noteId]);

	const secondaryActions = React.useMemo(() => {
		invariant(mode !== "edit" || Boolean(feedbackButtons), "Feedback buttons are required when mode is edit");

		if (editMode || (mode === "readonly" && summaryIsEmpty)) {
			return undefined;
		}

		const copyAction = (
			<Menu key="copy-menu">
				<Menu.Trigger>
					<IconButton variant="tertiary" size="small" title="Copy" icon={faClone} />
				</Menu.Trigger>
				<Menu.Content align="end">
					<Menu.Item onSelect={onCopySummary}>Copy Summary</Menu.Item>
					<Menu.Item onSelect={onCopyAll}>Copy entire Recap</Menu.Item>
				</Menu.Content>
			</Menu>
		);

		if (mode === "readonly") {
			return [copyAction];
		} else if (summaryIsEmpty) {
			return [<AddButton key="add-button" onClick={onEditSummary} />];
		} else {
			const actions = [];
			if (feedbackButtons) {
				actions.push(feedbackButtons(HighlightFeedbackSource.SUMMARY));
			}
			actions.push(copyAction);
			actions.push(<EditButton key="edit-button" onClick={onEditSummary} />);
			return actions;
		}
	}, [editMode, mode, summaryIsEmpty, onEditSummary, onCopySummary, onCopyAll, feedbackButtons]);

	if (summaryIsEmpty && !editMode) {
		return (
			<HighlightSection
				isCollapsed={isCollapsed}
				toggleCollapsed={toggleCollapsed}
				title="Summary"
				secondaryActions={secondaryActions}
			>
				{!recordingDuration || recordingDuration < 60 * 5 ? (
					<div className={styles.highlightEmptyState}>
						This meeting wasn&apos;t long enough to generate a summary.
						{mode !== "readonly" && (
							<>
								{" "}
								<ButtonLink onClick={onEditSummary}>Click to add a summary</ButtonLink>
							</>
						)}
					</div>
				) : (
					<div className={styles.highlightEmptyState}>
						No summary was automatically generated for this meeting.{" "}
						{mode !== "readonly" && (
							<>
								To <ButtonLink onClick={onEditSummary}>add a summary,</ButtonLink> click here.
							</>
						)}
					</div>
				)}
			</HighlightSection>
		);
	}

	return (
		<HighlightSection
			isCollapsed={isCollapsed}
			toggleCollapsed={toggleCollapsed}
			title="Summary"
			secondaryActions={secondaryActions}
		>
			{editMode ? (
				<main className={styles.textAreaWrapper}>
					<TextArea
						autoFocus
						ref={textareaRef}
						value={summary}
						onChange={onChangeSummary}
						className={styles.textArea}
						minRows={4}
						placeholder="Add a summary..."
					/>
					<div className={styles.btnWrapper}>
						<Button variant="tertiary" className={styles.actionBtn} onClick={onCancelEdit}>
							<FontAwesomeIcon icon={faTimes} />
						</Button>
						<Button
							variant="primary"
							className={styles.actionBtn}
							disabled={prevSummary.current === summary}
							onClick={onSaveEdit}
						>
							<FontAwesomeIcon icon={faCheck} />
						</Button>
					</div>
				</main>
			) : (
				<main ref={summarySectionRef} className={styles.summaryBody}>
					<SummaryText className={uxSimplificationEnabled ? styles.simplifiedTextStyles : undefined}>
						{summary}
					</SummaryText>
				</main>
			)}
		</HighlightSection>
	);
};

const ActionItemInHighlight = ({
	item,
	readOnly,
	recordingId,
	noteId,
	onTimestampClick: parentOnTimestampClick,
	videoDuration,
	isNew,
	doneCreation,
	noVideo,
}: {
	item: TranscriptActionItem;
	readOnly: boolean;
	recordingId: string;
	noteId: string | null;
	onTimestampClick?: (timestamp: number) => void;
	videoDuration: number | null;
	isNew?: boolean;
	doneCreation?: () => void;
	noVideo?: boolean;
}) => {
	invariant(!isNew || doneCreation, "doneCreation is required when isNew is true");

	const [inEditMode, setInEditMode] = React.useState<boolean>(isNew ?? false);
	const [actionItemText, setActionItemText] = React.useState<string>(
		isNew ? "" : actionItemToText(item, videoDuration),
	);
	const [loading, setLoading] = React.useState<boolean>(false);
	const [error, setError] = React.useState<string | null>(null);
	const textareaRef = React.useRef<HTMLTextAreaElement>(null);

	const { analyticsFingerprint, meetingRecordingSummaryId } = React.useContext(TranscriptModalContext);

	React.useEffect(() => {
		// Updates received from other users' edits
		if (!inEditMode) {
			const newActionItem = actionItemToText(item ?? "", videoDuration);
			setActionItemText(newActionItem);
		}
	}, [videoDuration, inEditMode, item]);

	const [expanded, setExpanded] = React.useState<boolean>(false);

	const onClickEntireItem = React.useCallback(() => setExpanded(prev => !prev), []);

	const insertActionItem = React.useCallback(() => {
		if (readOnly || !noteId) return;
		commitMutationPromise<HighlightsPanelCreateActionItemMutation>(environment, {
			mutation: insertActionItemMutation,
			variables: {
				input: {
					meetingRecordingId: recordingId,
					noteId: noteId,
					text: item.text,
					assigneeId: item.assignees?.[0]?.userId?.toString(),
				},
			},
		}).then(() => {
			toasty.success("Action item added successfully");
			dispatchAnalyticsTrack("[AI Copilot] Action Item Added to Note", { fingerprint: analyticsFingerprint });
		});
	}, [readOnly, recordingId, noteId, item.text, item.assignees, analyticsFingerprint]);

	const onTimestampClick = React.useCallback(
		(timestamp: number) => parentOnTimestampClick?.(timestamp),
		[parentOnTimestampClick],
	);

	const onEditActionItem = React.useCallback(() => {
		setInEditMode(true);

		// Hacky way to set the cursor to the end of the textarea when editing
		// The textarea is not rendered until editMode is true, so we need to wait
		// for the element to render to have access to the ref
		setTimeout(() => {
			textareaRef.current?.setSelectionRange(actionItemText.length, actionItemText.length);
		}, 0);
		dispatchAnalyticsTrack("[AI Copilot] Recap Edited", {
			fingerprint: analyticsFingerprint,
			action: "Started",
			/*  eslint-disable camelcase */
			recap_component: "Action Item",
			note_id: noteId,
			meeting_recording_summary_id: meetingRecordingSummaryId,
			/*  eslint-enable camelcase */
		});
	}, [actionItemText.length, analyticsFingerprint, meetingRecordingSummaryId, noteId]);

	const { copySection } = React.useContext(RecordingContext);
	const onCopyActionItem = React.useCallback(() => {
		copySection("SingleActionItem", { singleActionItemTimestamp: item.timestamp });
	}, [item, copySection]);

	const onChangeActionItemText = React.useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
		setActionItemText(event.target.value);
	}, []);

	const onInputActionItemText = React.useCallback((): void => {
		setError(null);
	}, []);

	const onSaveEdit = React.useCallback(() => {
		if (readOnly) return;
		setLoading(true);
		commitMutationPromise<HighlightsPanelSaveActionItemsMutation>(environment, {
			mutation: saveActionItemsMutation,
			variables: {
				recordingId,
				prevActionItemTimestamp: item.timestamp,
				actionItemText,
				isNew: Boolean(isNew),
			},
		})
			.then(res => {
				setLoading(false);
				if (!res.saveTranscriptActionItem) {
					toasty.error("Something went wrong, failed to update action item");
					return;
				}
				const { validationError, meetingRecording } = res.saveTranscriptActionItem;
				if (validationError) {
					setError(validationError);
				} else if (meetingRecording?.activeSummary?.actionItems) {
					if (isNew && doneCreation) {
						doneCreation();
					}
					setInEditMode(false);
					toasty.success("Action item updated successfully");
					dispatchAnalyticsTrack("[AI Copilot] Recap Edited", {
						fingerprint: analyticsFingerprint,
						action: "Saved",
						/*  eslint-disable camelcase */
						recap_component: "Action Item",
						note_id: noteId,
						meeting_recording_summary_id: meetingRecordingSummaryId,
						/*  eslint-enable camelcase */
					});
				}
			})
			.catch(errors => {
				setLoading(false);
				Sentry.captureException(errors[0]);
				toasty.error("Failed to update action item");
			});
	}, [
		actionItemText,
		analyticsFingerprint,
		doneCreation,
		isNew,
		item.timestamp,
		meetingRecordingSummaryId,
		noteId,
		readOnly,
		recordingId,
	]);

	const onCancelEdit = React.useCallback(() => {
		if (isNew && doneCreation) {
			return doneCreation();
		}
		setInEditMode(false);
		setError(null);
		setActionItemText(actionItemToText(item, videoDuration));
		dispatchAnalyticsTrack("[AI Copilot] Recap Edited", {
			fingerprint: analyticsFingerprint,
			action: "Cancelled",
			/*  eslint-disable camelcase */
			recap_component: "Action Item",
			note_id: noteId,
			meeting_recording_summary_id: meetingRecordingSummaryId,
			/*  eslint-enable camelcase */
		});
	}, [analyticsFingerprint, doneCreation, isNew, item, meetingRecordingSummaryId, noteId, videoDuration]);

	const onDeleteActionItem = React.useCallback(() => {
		commitMutationPromise<HighlightsPanelDeleteActionItemsMutation>(environment, {
			mutation: deleteActionItemsMutation,
			variables: {
				recordingId,
				actionItemTimestamp: item.timestamp,
			},
		})
			.then(() => {
				toasty.success("Action item deleted successfully");
				dispatchAnalyticsTrack("[AI Copilot] Recap Edited", {
					fingerprint: analyticsFingerprint,
					action: "Deleted",
					/*  eslint-disable camelcase */
					recap_component: "Action Item",
					note_id: noteId,
					meeting_recording_summary_id: meetingRecordingSummaryId,
					/*  eslint-enable camelcase */
				});
			})
			.catch(errors => {
				Sentry.captureException(errors[0]);
				toasty.error("Failed to delete action item");
			});
	}, [analyticsFingerprint, item.timestamp, meetingRecordingSummaryId, noteId, recordingId]);

	const onKebabMenuClick = React.useCallback(event => event.stopPropagation(), []);

	if (inEditMode) {
		return (
			<>
				<main className={styles.textAreaWrapper}>
					<TextArea
						autoFocus
						value={actionItemText}
						onChange={onChangeActionItemText}
						onInput={onInputActionItemText}
						className={styles.textArea}
						placeholder={`[${(videoDuration ?? 0) >= 3600 ? "00:" : ""}00:00] Assignee: Action item text`}
						ref={textareaRef}
					/>
					<div className={styles.btnWrapper}>
						<Button variant="tertiary" className={styles.actionBtn} onClick={onCancelEdit}>
							<FontAwesomeIcon icon={faTimes} />
						</Button>
						<Button
							variant="primary"
							className={styles.actionBtn}
							onClick={onSaveEdit}
							disabled={Boolean(error) || loading || actionItemText === actionItemToText(item, videoDuration)}
						>
							{loading ? <FontAwesomeIcon icon={faSpinnerThird} spin /> : <FontAwesomeIcon icon={faCheck} />}
						</Button>
					</div>
				</main>
				{error ? <p className={styles.errorText}>{error}</p> : null}
			</>
		);
	}

	return (
		<section className={cx(styles.actionItemContainer, { [styles.disabled]: readOnly })} onClick={onClickEntireItem}>
			<button
				className={cx(styles.actionItemIcon, {
					[styles.actionItemIconChecked]: Boolean(item.insertedBlockKey),
				})}
				onClick={insertActionItem}
				disabled={Boolean(item.insertedBlockKey) || readOnly}
			>
				<p>+</p>
			</button>
			<div className={styles.actionItem}>
				<p className={cx(styles.actionItemText, { [styles.condensedText]: !expanded })} title={item.text}>
					{
						// Add "to" before the text if there is an internal assignee
					}
					{item.assignees?.length && item.assignees[0]?.userId ? (
						<>
							<b>{item.assignees[0]?.fullName}</b> to {decapitalizeFirstLetter(item.text)}
						</>
					) : (
						item.text
					)}
				</p>
				<div className={styles.actionItemMeta}>
					<VideoTimestamp
						className={styles.actionItemTimestamp}
						timestamp={item.timestamp}
						videoDuration={videoDuration}
						onClick={onTimestampClick}
						disabled={noVideo}
					/>
					・
					{item.insertedBlockKey ? (
						item.assignees && item.assignees.length > 0 ? (
							`Added and assigned to ${
								item.assignees.length > 1 ? `multiple assignees` : (item.assignees[0]?.fullName ?? "")
							}`
						) : (
							"Added"
						)
					) : (
						<>
							<p className={styles.metaDefaultText}>{item.edited ? "Edited" : "AI-generated"}</p>
							{!readOnly && (
								<p className={styles.metaHoverText} onClick={insertActionItem}>
									Click to add to notes
								</p>
							)}
						</>
					)}
				</div>
			</div>
			{!readOnly && (
				<Menu.Root>
					<Menu.Trigger>
						<IconButton
							variant="tertiary"
							size="small"
							className={styles.actionItemKebab}
							title="Options"
							icon={faEllipsisV}
							onClick={onKebabMenuClick}
						/>
					</Menu.Trigger>
					<Menu.Content side="left">
						<Menu.Item icon={faPen} onSelect={onEditActionItem}>
							Edit
						</Menu.Item>
						<Menu.Item icon={faClone} onSelect={onCopyActionItem}>
							Copy
						</Menu.Item>
						<Menu.Separator />
						<Menu.Item icon={faTrashCan} className={styles.deleteOption} onSelect={onDeleteActionItem}>
							Delete
						</Menu.Item>
					</Menu.Content>
				</Menu.Root>
			)}
		</section>
	);
};

const emptyActionItem: TranscriptActionItem = {
	timestamp: 0,
	text: "",
	assignees: [],
	insertedBlockKey: null,
	edited: false,
};

export const ActionItemsHighlight = ({
	actionItems,
	feedbackButtons,
	videoDuration,
	onTimestampClick,
	readOnly,
	recordingId,
	noteId,
	noVideo,
	summaryCreatedAt,
}: {
	actionItems: TranscriptActionItems;
	videoDuration: number | null;
	onTimestampClick: (timestamp: number) => void;
	feedbackButtons?: (sectionName: HighlightFeedbackSource) => JSX.Element;
	readOnly: boolean;
	recordingId: string;
	noteId: string | null;
	noVideo?: boolean;
	summaryCreatedAt?: string;
}) => {
	const [isCollapsed, setIsCollapsed] = React.useState<boolean>(false);
	const toggleCollapsed = React.useCallback(() => {
		setIsCollapsed(!isCollapsed);
	}, [isCollapsed]);

	const isRealActionItemsFeatureFlagOn = useRealAiActionItemsIsOn();

	const [addingNew, setAddingNew] = React.useState<boolean>(false);

	const actionItemsAreEmpty = actionItems === null || actionItems.length === 0;

	const onAddActionItem = React.useCallback(() => {
		if (readOnly) return;
		setAddingNew(true);
	}, [readOnly]);

	const actionItemProps = {
		readOnly,
		recordingId,
		noteId,
		onTimestampClick,
		videoDuration,
	};

	const doneCreation = React.useCallback(() => {
		setAddingNew(false);
	}, []);

	const actionItemsSorted = React.useMemo(
		() => [...(actionItems ?? [])].sort((a, b) => a.timestamp - b.timestamp),
		[actionItems],
	);

	// Check if the summary was created after the cutoff date
	// TODO: Remove this once the feature flag is enabled in prod and some time has passed (ENG-496)
	const wasSummaryCreatedAfterFeatureFlag = React.useMemo(() => {
		// Define the date the feature flag will be enabled in prod - March 26th, 2025, 10am EST
		if (!summaryCreatedAt) return false;
		const FEATURE_FLAG_CUTOFF_DATE = new Date("2025-03-26T14:00:00Z");
		const summaryCreationDate = new Date(summaryCreatedAt);
		return summaryCreationDate >= FEATURE_FLAG_CUTOFF_DATE;
	}, [summaryCreatedAt]);

	const { copySection } = React.useContext(RecordingContext);
	const copyActionItems = React.useCallback(() => {
		copySection("ActionItems");
	}, [copySection]);

	const secondaryActions = React.useMemo(() => {
		invariant(readOnly || Boolean(feedbackButtons), "Feedback buttons are required when readOnly is false");

		if (addingNew) {
			return undefined;
		}

		let actions = [];
		if (feedbackButtons && !readOnly) {
			actions.push(feedbackButtons(HighlightFeedbackSource.ACTION_ITEMS));
		}

		if (actionItems && actionItems.length > 0) {
			actions.push(<CopyButton key="copy-button" onClick={copyActionItems} />);
		}

		if (!readOnly && !(noteId && recordingId && isRealActionItemsFeatureFlagOn && wasSummaryCreatedAfterFeatureFlag)) {
			actions.push(<AddButton key="add-button" onClick={onAddActionItem} />);
		}

		return actions;
	}, [
		readOnly,
		feedbackButtons,
		addingNew,
		actionItems,
		noteId,
		recordingId,
		isRealActionItemsFeatureFlagOn,
		copyActionItems,
		onAddActionItem,
		wasSummaryCreatedAfterFeatureFlag,
	]);

	if (noteId && recordingId && isRealActionItemsFeatureFlagOn && !readOnly && wasSummaryCreatedAfterFeatureFlag) {
		return (
			<HighlightSection
				title="Action Items"
				isCollapsed={isCollapsed}
				toggleCollapsed={toggleCollapsed}
				counter={actionItemsAreEmpty ? undefined : actionItems.length}
				secondaryActions={secondaryActions}
				dataTourTooltipTarget="action-items-highlight"
			>
				<React.Suspense
					fallback={<LoadingSpinner size="lg" inline dimmer={false} className={styles.sectionLoadingSpinner} />}
				>
					<AIActionItemsList />
				</React.Suspense>
			</HighlightSection>
		);
	} else {
		return (
			<HighlightSection
				title="Action Items"
				isCollapsed={isCollapsed}
				toggleCollapsed={toggleCollapsed}
				counter={actionItemsAreEmpty ? undefined : actionItems.length}
				secondaryActions={secondaryActions}
				dataTourTooltipTarget="action-items-highlight"
			>
				{actionItemsAreEmpty ? (
					addingNew ? (
						<ActionItemInHighlight
							item={emptyActionItem}
							isNew
							doneCreation={doneCreation}
							noVideo={noVideo}
							{...actionItemProps}
						/>
					) : (
						<div className={styles.highlightEmptyState}>
							{emptyHighlightSectionText(videoDuration, "action items")}
							{!readOnly && (
								<>
									To <ButtonLink onClick={onAddActionItem}>add an action item,</ButtonLink> add a timestamp in brackets
									followed by the action item text. An optional assignee name can be added before the text separated by
									a colon, e.g. {`[${(videoDuration ?? 0) >= 3600 ? "00:" : ""}01:23] Assignee: Action Item One`}
								</>
							)}
						</div>
					)
				) : (
					<main>
						{actionItemsSorted.map(item => (
							<ActionItemInHighlight key={item.timestamp} item={item} noVideo={noVideo} {...actionItemProps} />
						))}
						{addingNew && (
							<ActionItemInHighlight
								item={emptyActionItem}
								isNew
								doneCreation={doneCreation}
								noVideo={noVideo}
								{...actionItemProps}
							/>
						)}
					</main>
				)}
			</HighlightSection>
		);
	}
};

export const DecisionsHighlight = ({
	decisionsKey,
	videoDuration,
	onTimestampClick,
	feedbackButtons,
	onUpdateDecisions,
	mode,
	noVideo,
	recordingId,
}: {
	decisionsKey: HighlightsPanel_DecisionsHighlight_decisions$key | null;
	videoDuration: number | null;
	onTimestampClick?: (timestamp: number) => void;
	feedbackButtons?: (sectionName: HighlightFeedbackSource) => JSX.Element;
	onUpdateDecisions?: (decisionsText: string) => Promise<HighlightsPanelSaveDecisionsMutation$data>;
	mode: "edit" | "readonly";
	noVideo?: boolean;
	recordingId: string;
}) => {
	const decisions = useFragment(
		graphql`
			fragment HighlightsPanel_DecisionsHighlight_decisions on TranscriptDecision @relay(plural: true) {
				timestamp
				text
				context
			}
		`,
		decisionsKey,
	);

	const decisionsIsEmpty = decisions === null || decisions.length === 0;

	const [editMode, setEditMode] = React.useState<boolean>(false);
	const [decisionsText, setDecisionsText] = React.useState<string>(keyMomentsToText(decisions ?? [], videoDuration));
	const [loading, setLoading] = React.useState<boolean>(false);
	const [error, setError] = React.useState<string | null>(null);
	const prevDecisionsText = React.useRef<string>(decisionsText);
	const textareaRef = React.useRef<HTMLTextAreaElement>(null);
	const { analyticsFingerprint, noteId, meetingRecordingSummaryId } = React.useContext(TranscriptModalContext);

	const decisionsSectionRef = React.useRef<HTMLDivElement>(null);

	const [isCollapsed, setIsCollapsed] = React.useState<boolean>(false);
	const toggleCollapsed = React.useCallback(() => {
		if (editMode) return;
		setIsCollapsed(!isCollapsed);
	}, [editMode, isCollapsed]);

	React.useEffect(() => {
		// Updates received from other users' edits
		if (!editMode) {
			const newDecisions = keyMomentsToText(decisions ?? [], videoDuration);
			setDecisionsText(newDecisions);
			prevDecisionsText.current = newDecisions;
		}
	}, [editMode, decisions, videoDuration]);

	const onEditDecisions = React.useCallback(() => {
		if (mode === "readonly") return;

		setEditMode(true);
		setIsCollapsed(false);

		// Hacky way to set the cursor to the end of the textarea when editing
		// The textarea is not rendered until editMode is true, so we need to wait
		// for the element to render to have access to the ref
		setTimeout(() => {
			textareaRef.current?.setSelectionRange(decisionsText.length, decisionsText.length);
			textareaRef.current?.scrollIntoView({ block: "center" });
		}, 0);
		dispatchAnalyticsTrack("[AI Copilot] Recap Edited", {
			fingerprint: analyticsFingerprint,
			action: "Started",
			/*  eslint-disable camelcase */
			recap_component: "Decision",
			note_id: noteId,
			meeting_recording_summary_id: meetingRecordingSummaryId,
			/*  eslint-enable camelcase */
		});
	}, [analyticsFingerprint, decisionsText.length, meetingRecordingSummaryId, noteId, mode]);

	const onChangeDecisionsText = React.useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
		setDecisionsText(event.target.value);
	}, []);

	const onInputDecisionsText = React.useCallback((): void => {
		setError(null);
	}, []);

	const onSaveEdit = React.useCallback(() => {
		if (mode === "readonly") return;
		setLoading(true);
		onUpdateDecisions?.(decisionsText)
			.then(res => {
				setLoading(false);

				if (!res.saveDecisions) {
					toasty.error("Something went wrong, failed to update chapters");
					return;
				}

				const { validationError, meetingRecording } = res.saveDecisions;

				if (validationError) {
					setError(validationError);
				} else if (meetingRecording?.activeSummary?.decisions) {
					setEditMode(false);
					prevDecisionsText.current = keyMomentsToText(meetingRecording.activeSummary.decisions, videoDuration);
					toasty.success("Decisions updated successfully");
					decisionsSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
					dispatchAnalyticsTrack("[AI Copilot] Recap Edited", {
						fingerprint: analyticsFingerprint,
						action: "Saved",
						/*  eslint-disable camelcase */
						recap_component: "Decision",
						note_id: noteId,
						meeting_recording_summary_id: meetingRecordingSummaryId,
						/*  eslint-enable camelcase */
					});
				}
			})
			.catch(errors => {
				setLoading(false);
				Sentry.captureException(errors[0]);
				toasty.error("Something went wrong, failed to update decisions");
			});
	}, [analyticsFingerprint, decisionsText, meetingRecordingSummaryId, noteId, onUpdateDecisions, mode, videoDuration]);

	const onCancelEdit = React.useCallback(() => {
		setEditMode(false);
		setError(null);
		setDecisionsText(prevDecisionsText.current);
		dispatchAnalyticsTrack("[AI Copilot] Recap Edited", {
			fingerprint: analyticsFingerprint,
			action: "Cancelled",
			/*  eslint-disable camelcase */
			recap_component: "Decision",
			note_id: noteId,
			meeting_recording_summary_id: meetingRecordingSummaryId,
			/*  eslint-enable camelcase */
		});
	}, [analyticsFingerprint, meetingRecordingSummaryId, noteId]);

	const onClick = React.useCallback(
		(timestamp: number) => {
			onTimestampClick?.(timestamp);
			dispatchAnalyticsTrack("[AI Copilot] Decision Clicked", {
				fingerprint: analyticsFingerprint,
				ux_location: "highlights", // eslint-disable-line camelcase
			});
		},
		[analyticsFingerprint, onTimestampClick],
	);

	const { copySection } = React.useContext(RecordingContext);
	const onCopyDecisions = React.useCallback(() => {
		copySection("Decisions");
	}, [copySection]);

	const secondaryActions = React.useMemo(() => {
		invariant(mode !== "edit" || Boolean(feedbackButtons), "Feedback buttons are required when mode is edit");

		if (editMode || (mode === "readonly" && decisionsIsEmpty)) {
			return undefined;
		}

		const actions = [];
		if (feedbackButtons && mode !== "readonly") {
			actions.push(feedbackButtons(HighlightFeedbackSource.DECISIONS));
		}

		if (decisionsIsEmpty) {
			actions.push(<AddButton key="add-button" onClick={onEditDecisions} />);
		} else {
			actions.push(<CopyButton key="copy-button" onClick={onCopyDecisions} />);

			if (mode !== "readonly") {
				actions.push(<EditButton key="edit-button" onClick={onEditDecisions} />);
			}
		}
		return actions;
	}, [editMode, mode, decisionsIsEmpty, feedbackButtons, onEditDecisions, onCopyDecisions]);

	const summaryXrayEnabled = useFeatureIsOn("summarizer-xray");
	const isPublic = useIsPublic();
	return (
		<HighlightSection
			title="Decisions"
			isCollapsed={isCollapsed}
			toggleCollapsed={toggleCollapsed}
			counter={decisionsIsEmpty ? undefined : decisions.length}
			secondaryActions={secondaryActions}
		>
			{decisionsIsEmpty && !editMode ? (
				<div className={styles.highlightEmptyState}>
					{emptyHighlightSectionText(videoDuration, "decisions")}
					{mode !== "readonly" && (
						<>
							To <ButtonLink onClick={onEditDecisions}>add a decision,</ButtonLink> add a timestamp in brackets followed
							by a title, e.g. {`[${(videoDuration ?? 0) >= 3600 ? "00:" : ""}01:23] Decision One`}
						</>
					)}
				</div>
			) : (
				<>
					{editMode ? (
						<>
							<main className={cx(styles.textAreaWrapper, { [styles.error]: error })}>
								<TextArea
									autoFocus
									ref={textareaRef}
									value={decisionsText}
									onChange={onChangeDecisionsText}
									onInput={onInputDecisionsText}
									className={styles.textArea}
									minRows={4}
									placeholder={`[${(videoDuration ?? 0) >= 3600 ? "00:" : ""}00:00] Add a decision`}
								/>
								<div className={styles.btnWrapper}>
									<Button variant="tertiary" className={styles.actionBtn} disabled={loading} onClick={onCancelEdit}>
										<FontAwesomeIcon icon={faTimes} />
									</Button>
									<Button
										variant="primary"
										className={styles.actionBtn}
										disabled={!!error || loading || decisionsText === prevDecisionsText.current}
										onClick={onSaveEdit}
									>
										{loading ? <FontAwesomeIcon icon={faSpinnerThird} spin /> : <FontAwesomeIcon icon={faCheck} />}
									</Button>
								</div>
							</main>
							{error ? <p className={styles.errorText}>{error}</p> : null}
						</>
					) : (
						<main className={styles.keyPointsWrapper} ref={decisionsSectionRef}>
							<ul className={styles.keyPointsBullets}>
								{decisions &&
									decisions.map((decision, index) => (
										// eslint-disable-next-line react/no-array-index-key
										<li key={`decision-item-${index}`}>
											<section key={`${decision.text}-${decision.timestamp}`} className={styles.keyPointsSection}>
												<div className={styles.bulletTextWrapper}>
													<span className={styles.chapterTitle}>
														<Markdown options={HIGHLIGHTS_MARKDOWN_OPTIONS}>{decision.text}</Markdown>
													</span>
													<VideoTimestamp
														className={chapterStyles.chapterTimestamp}
														timestamp={decision.timestamp}
														videoDuration={videoDuration}
														onClick={onClick}
														disabled={noVideo}
														isInlineTimestamp={true}
													/>
												</div>
												{summaryXrayEnabled && !isPublic && (
													<ContextPopover
														context={decision.context ?? ""}
														onTimestampClick={onClick}
														itemText={decision.text}
														timestamp={decision.timestamp ? formatTimestamp(decision.timestamp) : undefined}
														recordingId={recordingId}
														lookupKey={index}
														chapterRowId={`decision-item-${index}`}
														itemType="decision"
													>
														<Tooltip content="X-Ray">
															<FontAwesomeIcon
																icon={faMagnifyingGlassPlus}
																className={styles.contextIcon}
																tabIndex={0}
															/>
														</Tooltip>
													</ContextPopover>
												)}
											</section>
										</li>
									))}
							</ul>
						</main>
					)}
				</>
			)}
		</HighlightSection>
	);
};

export const TrackersHighlight = ({
	smartTrackers,
	videoDuration,
	onTimestampClick,
}: {
	smartTrackers: MeetingRecordingSmartTrackers;
	videoDuration: number | null;
	onTimestampClick?: (timestamp: number) => void;
}) => {
	const [isCollapsed, setIsCollapsed] = React.useState<boolean>(false);
	const toggleCollapsed = React.useCallback(() => {
		setIsCollapsed(!isCollapsed);
	}, [isCollapsed]);

	const { analyticsFingerprint } = React.useContext(TranscriptModalContext);

	const onTrackerClick = React.useCallback(
		(timestamp: number) => {
			onTimestampClick?.(timestamp);
			dispatchAnalyticsTrack("[AI Copilot] Tracker Clicked", {
				fingerprint: analyticsFingerprint,
				ux_location: "highlights", // eslint-disable-line camelcase
			});
		},
		[analyticsFingerprint, onTimestampClick],
	);

	return (
		<HighlightSection title="Trackers" isCollapsed={isCollapsed} toggleCollapsed={toggleCollapsed}>
			<main className={styles.chaptersBody}>
				{smartTrackers.map(tracker => (
					<section key={tracker.trackerName} className={styles.trackersChapter}>
						<FontAwesomeIcon icon={faLocation} className={styles.chapterIcon} />
						<div className={styles.chapterContent}>
							<p className={styles.chapterTitle}>{tracker.trackerName}</p>
							{tracker.matches.map(match => (
								<VideoTimestamp
									key={`${tracker.trackerName}-${match.startTimestamp}`}
									className={styles.chapterTimestamp}
									timestamp={match.startTimestamp}
									videoDuration={videoDuration}
									onClick={onTrackerClick}
								/>
							))}
						</div>
					</section>
				))}
			</main>
		</HighlightSection>
	);
};

export const Highlights = React.memo(function Highlights({
	note: noteKey,
	meetingRecording: meetingRecordingKey,
	viewer: viewerKey,
	videoDuration,
	onTimestampClick,
	onDislike,
	onGenerateTemplate,
	chaptersRef,
	thumbnailTrack,
	narrowViewport,
	trackLowLanguageConfidenceScoreBannerShown,
}: {
	note: HighlightsPanel_note$key | null;
	meetingRecording: HighlightsPanel_meetingRecording$key;
	viewer: HighlightsPanel_viewer$key;
	videoDuration: number | null;
	onTimestampClick: (timestamp: number) => void;
	onDislike: (source: HighlightFeedbackSource) => void;
	onGenerateTemplate: (templateType: RecapTemplateType) => void;
	chaptersRef: React.Ref<ChaptersPanelRef>;
	thumbnailTrack: TextTrack | null;
	narrowViewport?: boolean | null;
	trackLowLanguageConfidenceScoreBannerShown: (analyticsData: LowLanguageConfidenceScoreAnalyticsDataType) => void;
}) {
	const { analyticsFingerprint } = React.useContext(BillingContext);
	const appContext = React.useContext(AppContext);
	// UX Simplification
	const uxSimplificationEnabled = appContext.user?.uxSimplificationEnabled;

	const note = useFragment(
		graphql`
			fragment HighlightsPanel_note on Note {
				id
				title
				isLocked
				viewerCanEdit
			}
		`,
		noteKey,
	);

	const meetingRecording = useFragment(
		graphql`
			fragment HighlightsPanel_meetingRecording on MeetingRecording @argumentDefinitions(public: { type: "Boolean!" }) {
				__typename
				id
				hasExternalAttendees @skip(if: $public)
				activeSummary {
					summary
					createdAt
					actionItems {
						timestamp
						text
						assignees {
							userId
							fullName
						}
						insertedBlockKey
						edited
					}
					decisions {
						...HighlightsPanel_DecisionsHighlight_decisions
					}
					chapters {
						timestamp
						title
						bulletPoints {
							timestamp
							text
							textShort
							context
						}
					}
					templatedRecaps @skip(if: $public)
					activeRecapTemplate @skip(if: $public)
				}
				title
				meetingCallEnd
				recordingAndTranscriptWillBeDeleted
				transcriptDeletionReason
				recordingDataAutoDeletionDate
				meetingRecordedVideoDuration
				...utils_AddedOrAvailableUntil_meetingRecording
				userVideoProgress
				smartTrackers @skip(if: $public) {
					trackerName
					matches {
						startTimestamp
						endTimestamp
					}
				}
				ownerAccount {
					__typename
				}
				showLowLanguageConfidenceRegenerationBanner
				...TranscriptPanel_UnlabeledSpeakersWarning_meetingRecording
				...TranscriptPanel_RelabelMultipleSpeakersModal_meetingRecording @arguments(public: $public)
				...TranscriptPanel_LowLanguageConfidenceRegenerationBanner_meetingRecording @arguments(public: $public)
			}
		`,
		meetingRecordingKey,
	);

	const { account } = useFragment(
		graphql`
			fragment HighlightsPanel_viewer on Viewer {
				account {
					__typename
					allowAiFeatures
					billingAccount {
						hasAccessToRecapTemplates: hasAccessToFeature(feature: recap_templates)
					}
				}
			}
		`,
		viewerKey,
	);

	const recapTemplatesPaywalled = !account.billingAccount.hasAccessToRecapTemplates;

	const isPublic = useIsPublic();
	const isXWC = meetingRecording.ownerAccount.__typename === "ExternalAccount";
	const isGuest = window.INITIAL_STATE.user?.type === "GUEST";

	const regenerationBannerIsVisible =
		account.allowAiFeatures &&
		note?.viewerCanEdit &&
		!note?.isLocked &&
		!isXWC &&
		!isGuest &&
		!isPublic &&
		meetingRecording.__typename === "MeetingRecording" &&
		meetingRecording.showLowLanguageConfidenceRegenerationBanner === true;

	const updateSummary = React.useCallback(
		(summary: string) => {
			return commitMutationPromise<HighlightsPanelSaveSummaryMutation>(environment, {
				mutation: saveSummaryMutation,
				variables: {
					recordingId: meetingRecording.id,
					summary: summary,
				},
			});
		},
		[meetingRecording.id],
	);

	const updateDecisions = React.useCallback(
		(decisionsText: string) => {
			return commitMutationPromise<HighlightsPanelSaveDecisionsMutation>(environment, {
				mutation: saveDecisionsMutation,
				variables: {
					recordingId: meetingRecording.id,
					decisionsText: decisionsText,
				},
			});
		},
		[meetingRecording.id],
	);

	const updateKeyMoments = React.useCallback(
		(templateType: RecapTemplateType, templateSection: string) => (keyMomentsText: string) => {
			return commitMutationPromise<HighlightsPanelSaveKeyMomentsMutation>(environment, {
				mutation: saveKeyMomentsMutation,
				variables: {
					recordingId: meetingRecording.id,
					templateType,
					templateSection,
					keyMomentsText,
				},
			});
		},
		[meetingRecording.id],
	);

	const narrowViewportHighlight = useIsNarrowViewport() || narrowViewport;
	const regenerateButtons = React.useCallback(
		(sectionName: HighlightFeedbackSource) => (
			<RegenerateButtons
				key="regenerate-buttons"
				clickDislike={onDislike}
				recordingId={meetingRecording.id}
				recordingTitle={meetingRecording.title}
				sectionName={sectionName}
			/>
		),
		[meetingRecording.id, meetingRecording.title, onDislike],
	);

	const { templatedRecaps, activeRecapTemplate } = meetingRecording.activeSummary || {};

	const initialTemplateOption = React.useMemo(() => {
		return activeRecapTemplate && !recapTemplatesPaywalled && !isEmpty(templatedRecaps[activeRecapTemplate])
			? TEMPLATE_OPTIONS.find(option => option.value === activeRecapTemplate)
			: null;
	}, [activeRecapTemplate, recapTemplatesPaywalled, templatedRecaps]);

	const [selectedTemplate, setSelectedTemplate] = React.useState<TemplateOption>(
		initialTemplateOption ?? TEMPLATE_OPTIONS[0],
	);

	const templateData = React.useMemo(() => {
		if (isNil(activeRecapTemplate)) return null;
		if (!isEmpty(templatedRecaps[activeRecapTemplate])) {
			return templatedRecaps[activeRecapTemplate];
		}
		return null;
	}, [activeRecapTemplate, templatedRecaps]);

	const onChangeTemplate = React.useCallback(
		(option: SingleValue<TemplateOption>) => {
			if (recapTemplatesPaywalled) {
				showFeatureModal("recap_templates");
				return;
			}
			if (option === null) {
				return;
			} else if (option.value === "GENERAL") {
				saveActiveTemplate(meetingRecording.id, null);
				setSelectedTemplate(option);
			} else if (isEmpty(templatedRecaps[option.value])) {
				onGenerateTemplate(option.value);
			} else {
				saveActiveTemplate(meetingRecording.id, option.value);
				setSelectedTemplate(option);
			}

			if (option.value !== "GENERAL") {
				dispatchAnalyticsTrack("[AI Copilot] Selected AI Template", {
					fingerprint: analyticsFingerprint,
					template: option.value,
				});
			}
		},
		[analyticsFingerprint, meetingRecording.id, onGenerateTemplate, recapTemplatesPaywalled, templatedRecaps],
	);

	const isSmartTrackersFeatureFlagEnabled = useTrackersFeatureFlagOn();

	const openRelabelSpeakersModal = React.useCallback(() => {
		openRelabelMultipleSpeakersModal(meetingRecording, analyticsFingerprint);
	}, [analyticsFingerprint, meetingRecording]);

	const mode = useMemo(() => {
		return !note?.viewerCanEdit ? "readonly" : "edit";
	}, [note]);

	let content = (
		<>
			{note?.id && regenerationBannerIsVisible && (
				<LowLanguageConfidenceRegenerationBanner
					meetingRecording={meetingRecording}
					noteId={note.id}
					trackLowLanguageConfidenceScoreBannerShown={trackLowLanguageConfidenceScoreBannerShown}
					className={uxSimplificationEnabled ? transcriptStyles.simplifiedBannerMargin : ""}
				/>
			)}
			<UnlabeledSpeakersWarning
				meetingRecording={meetingRecording}
				onLabelSpeakersClick={openRelabelSpeakersModal}
				className={uxSimplificationEnabled ? transcriptStyles.simplifiedBannerMargin : ""}
			/>

			<SummaryHighlight
				mode={mode}
				recordingDuration={meetingRecording.meetingRecordedVideoDuration}
				summary={meetingRecording.activeSummary?.summary || ""}
				onUpdateSummary={updateSummary}
				feedbackButtons={mode === "edit" ? regenerateButtons : undefined}
			/>

			{narrowViewportHighlight && meetingRecording.activeSummary && (
				<ChaptersPanel
					collapsible
					recordingId={meetingRecording.id}
					ref={chaptersRef}
					chapters={meetingRecording.activeSummary.chapters}
					userVideoProgress={meetingRecording.userVideoProgress}
					onTimestampClick={onTimestampClick}
					onDislike={onDislike}
					videoDuration={videoDuration}
					thumbnailTrack={thumbnailTrack}
					mode={mode}
					recordingTitle={meetingRecording.title}
					chaptersScrollDisabled={narrowViewportHighlight}
				/>
			)}

			{meetingRecording.activeSummary && (
				<>
					<ActionItemsHighlight
						readOnly={!!isPublic || !note || !note.viewerCanEdit}
						actionItems={meetingRecording.activeSummary.actionItems}
						onTimestampClick={meetingRecording.transcriptDeletionReason ? noop : onTimestampClick}
						videoDuration={meetingRecording.meetingRecordedVideoDuration ?? videoDuration}
						feedbackButtons={regenerateButtons}
						recordingId={meetingRecording.id}
						noteId={note?.id || null}
						summaryCreatedAt={meetingRecording.activeSummary.createdAt}
					/>
					<DecisionsHighlight
						mode={mode}
						decisionsKey={meetingRecording.activeSummary.decisions}
						videoDuration={meetingRecording.meetingRecordedVideoDuration ?? videoDuration}
						onTimestampClick={meetingRecording.transcriptDeletionReason ? undefined : onTimestampClick}
						onUpdateDecisions={updateDecisions}
						feedbackButtons={mode === "edit" ? regenerateButtons : undefined}
						recordingId={meetingRecording.id}
					/>
				</>
			)}
			{isSmartTrackersFeatureFlagEnabled &&
				meetingRecording.smartTrackers &&
				meetingRecording.smartTrackers.length > 0 &&
				!isPublic &&
				!appContext.user?.isGuest && (
					<TrackersHighlight
						smartTrackers={meetingRecording.smartTrackers}
						videoDuration={videoDuration}
						onTimestampClick={onTimestampClick}
					/>
				)}
		</>
	);

	if (selectedTemplate.value !== "GENERAL" && templateData) {
		content = (
			<>
				{Object.entries(templateData as RecapTemplate).map(([sectionName, bullets]) => {
					return (
						<GenericHighlight
							key={`${selectedTemplate.value}-${sectionName}`}
							sectionName={sectionName}
							keyMoments={bullets}
							videoDuration={videoDuration}
							onUpdateKeyMoments={updateKeyMoments(selectedTemplate.value, sectionName)}
							templateType={selectedTemplate.value}
							onTimestampClick={onTimestampClick}
							readOnly={!!isPublic || !note?.viewerCanEdit}
						/>
					);
				})}

				{/* Add chapter component at the bottom in case it is narrow width */}
				{narrowViewportHighlight && meetingRecording.activeSummary && (
					<ChaptersPanel
						collapsible
						recordingId={meetingRecording.id}
						ref={chaptersRef}
						chapters={meetingRecording.activeSummary.chapters}
						userVideoProgress={meetingRecording.userVideoProgress}
						onTimestampClick={onTimestampClick}
						onDislike={onDislike}
						videoDuration={videoDuration}
						thumbnailTrack={thumbnailTrack}
						mode={mode}
						recordingTitle={meetingRecording.title}
						chaptersScrollDisabled={narrowViewportHighlight}
					/>
				)}
			</>
		);
	}

	return (
		<>
			{meetingRecording.hasExternalAttendees && !isPublic && !uxSimplificationEnabled && (
				<div className={styles.templateSelector}>
					<p>Recap template:</p>
					<Selector condensed options={TEMPLATE_OPTIONS} value={selectedTemplate} onChange={onChangeTemplate} />
				</div>
			)}
			{content}
		</>
	);
});

export const HIGHLIGHTS_MARKDOWN_OPTIONS: React.ComponentProps<typeof Markdown>["options"] = {
	disableParsingRawHTML: true,
	forceInline: true,
	overrides: {
		img: {
			component: props => <>{props.title ?? props.src}</>,
		},
		a: {
			component: props => <>{props.href}</>,
		},
	},
};
